import React from 'react';
import { ArrayHelpers, FloatingMenu, Forms, SystemText } from 'e4shared';
import { useSelector } from 'react-redux';
import './metricPicker.css';
import { hasModule } from 'helpers/profile';

export const GetIntegrationMetricName = (metric, user) => {
    const int = user.integrations.find(i => i.ref === metric.ref);
    let name = int?.name;

    if (metric.ref === 'ODBC') {
        name = int.extra || SystemText('Admin/Integrations/Odbc/Fields/Name/Default');
    }
    if (metric.ref === 'e4') {
        name = int.extra || SystemText('Admin/Integrations/E4/Fields/Name/Default');
    }

    return name;
};

export const MetricPicker = ({
    limitTypes,
    label,
    placeholder,
    error,
    mandatory,
    onChange,
    value,
    help,
    hideSubMetrics,
    hideRatioMetrics,
    allowRealtime,
    showAverage,
}) => {
    const [type, setType] = React.useState('');
    const [metricId, setMetricId] = React.useState(null);
    const [subMetricId, setSubMetricId] = React.useState(null);
    const [subMetricValues, setSubMetricValues] = React.useState([]);
    const [filteredMetrics, setFilteredMetrics] = React.useState([]);
    const [types, setTypes] = React.useState([]);
    const [average, setAverage] = React.useState(false);
    const [averageMetric, setAverageMetric] = React.useState(false);
    const [init, setInit] = React.useState(false);

    const metrics = useSelector(state => state.dashboard.metrics);
    const user = useSelector(state => state.user.user);
    const showFormHelp = useSelector(state => state.ui.showFormHelp);
    const selectedMetric =
        metrics?.length && metricId
            ? metrics
                  .filter(m => m.ref === type || (type === 'CRM' && m.integration === type))
                  .filter(m => allowRealtime || !m.realTime)
                  .find(m => m.id === metricId)
            : null;

    React.useEffect(() => {
        if (
            metricId &&
            metricId.toString().indexOf('RT') < 0 &&
            ArrayHelpers.existsAndHasLength(filteredMetrics) &&
            !filteredMetrics.some(x => x.id === metricId)
        ) {
            onChange(null);
        }
    }, [metricId, filteredMetrics]);

    React.useEffect(() => {
        if (type === 'CRM') {
            const selectedMetric =
                metrics?.length && metricId
                    ? metrics
                          .filter(m => m.ref === type || (type === 'CRM' && m.integration === type))
                          .filter(m => allowRealtime || !m.realTime)
                          .find(m => m.id === metricId)
                    : null;

            (selectedMetric?.calc_Avg ?? false) && setAverage(true);
            setAverageMetric(selectedMetric?.calc_Avg ?? false);
        } else {
            setAverageMetric(false);
        }
    }, [type, metricId]);

    React.useEffect(() => {
        init &&
            onChange({
                type,
                average,
                metricId: isNaN(metricId) ? 1 : metricId,
                subMetricId,
                subMetricValues,
                metricRef: (metricId || '').toString(),
            });
    }, [average, metricId, subMetricId, subMetricValues]);

    React.useEffect(() => {
        if (!init) {
            if (!hasModule(user, 'integrations')) value.type = 'e4';
            if (value) {
                setType(value.type);
                setAverage(value.average);
                setMetricId(value.metricRef?.indexOf('RT-') >= 0 ? value.metricRef : value.metricId);
                setSubMetricId(value.subMetricId);
                setSubMetricValues(value.subMetricValues);
            }
            setInit(true);
        }
    }, [value]);

    React.useEffect(() => {
        const temp = [...metrics];

        setTypes(
            temp
                .filter(
                    m =>
                        (m.integration === 'CRM' && user.crmIntegration) ||
                        (m.integration === 'CRM' && user.crmIntegration && allowRealtime && m.realTime) ||
                        user.integrations.some(i => i.ref === m.ref)
                )
                .reduce((all, metric) => {
                    return all.some(a => a.ref === metric.integration)
                        ? all
                        : all.concat([
                              {
                                  id: ['CRM'].some(x => x === metric.integration) ? metric.integration : metric.ref,
                                  ref: metric.integration,
                                  sort: metric.integration === 'CRM' ? 1 : metric.integration === 'e4' ? -1 : 0,
                                  name:
                                      metric.integration === 'CRM'
                                          ? user.crmIntegration
                                          : GetIntegrationMetricName(metric, user),
                              },
                          ]);
                }, [])
                .filter(m => m.id)
                .filter(m => hasModule(user, 'integrations') || m.id === 'e4')
                .sort((a, b) => (a.sort > b.sort ? -1 : a.sort < b.sort ? 1 : a.name > b.name ? 1 : -1))
        );

        const filtered = temp
            .filter(m => m.ref === type || (type === 'CRM' && m.integration === type))
            .filter(m => allowRealtime || !m.realTime)
            .filter(m => !hideRatioMetrics || !m.ratio_Id1)
            .sort((a, b) => (a.name.trim().toLowerCase() > b.name.trim().toLowerCase() ? 1 : -1));

        setFilteredMetrics([...filtered]);
    }, [metrics, type]);

    const text = {
        realtime: SystemText('General/Controls/MetricPicker/RealTime'),
        notSet: SystemText('General/Controls/MetricPicker/NotSet'),
        select: SystemText('General/Controls/MetricPicker/Type/Select'),
        selected: x => SystemText('General/Controls/MetricPicker/Type/Selected', x),
        noIntegrations: SystemText('General/Controls/MetricPicker/NoIntegrations'),
        help: SystemText('General/Controls/MetricPicker/Help'),
        fields: {
            metric: {
                help: SystemText('General/Controls/MetricPicker/Fields/Metric/Help'),
                placeholder: SystemText('General/Controls/MetricPicker/Fields/Metric/Placeholder'),
            },
            field: {
                placeholder: SystemText('General/Controls/MetricPicker/Fields/Field/Placeholder'),
            },
            value: {
                placeholder: SystemText('General/Controls/MetricPicker/Fields/Value/Placeholder'),
            },
            average: {
                label: SystemText('General/Controls/MetricPicker/Fields/Average/Label'),
                help: SystemText('General/Controls/MetricPicker/Fields/Average/Help'),
                total: SystemText('General/Controls/MetricPicker/Fields/Average/Buttons/Total'),
                average: SystemText('General/Controls/MetricPicker/Fields/Average/Buttons/Average'),
            },
        },
    };

    const metricExtra = metric => {
        return metric.realTime ? () => <i className="text-warning text-sm fa fa-clock" title={text.realtime} /> : null;
    };

    const metricSafeId = metric => {
        return metric.realTime && metric.id ? `RT-${metric.id.toString().replace('RT-', '')}` : metric.id;
    };

    const metricImage = metric => {
        switch (metric.id) {
            case 'CRM':
                return (
                    <img
                        className="mr10"
                        alt=""
                        loading="lazy"
                        src={`/dist/img/${user.crmIntegration?.replace(' ', '_')?.toLowerCase()}-icon.png`}
                    />
                );
            default:
                return <img alt="" loading="lazy" className="mr10" src={`/dist/img/${metric.id?.toLowerCase()}-icon.png`} />;
        }
    };

    const allowed = x => !limitTypes || !limitTypes.some(t => t === x);
    const typeForName = types?.find(t => t.id === type);
    const typeName = typeForName ? text.selected(typeForName.name) : text.select;

    const pickButton = (x, i) =>
        allowed(x.id) && (
            <li
                data-testid={`pick-${x.ref}`}
                key={`pick-${x.id}`}
                onClick={() => {
                    if (x.id !== type) {
                        setMetricId(null);
                        setSubMetricValues([]);
                        setSubMetricId(null);
                        setType(x.id);
                    }
                }}
                className="flex flex-center"
            >
                {metricImage(x)}
                {x.name}
            </li>
        );

    const tightCount = subMetricId ? 3 : selectedMetric?.extra_Fields?.length > 0 ? 2 : 1;

    const subMetrics =
        selectedMetric?.extra_Fields
            .find(f => f.id === subMetricId)
            ?.values?.map(f => ({ name: f || text.notSet, id: f })) || [];

    return !types.some(t => allowed(t.id)) ? (
        <div className="mb20">{text.noIntegrations}</div>
    ) : (
        <div className="metric-picker">
            <div className={`form-group ${error && 'has-error'}`}>
                <label className="mb5">
                    {label}
                    {mandatory && <span className="mandatory">*</span>}
                </label>
                {showFormHelp && (help || text.help) && <span className="form-control-help">{help || text.help}</span>}
                <div className="flex mbm10">
                    <FloatingMenu buttonText={typeName}>{types.map((t, i) => pickButton(t, i))}</FloatingMenu>
                    <div className={`flex-grow ml10 tight tight-${tightCount}`}>
                        <div>
                            <Forms.Select
                                label={null}
                                id="select-first"
                                mandatory={mandatory}
                                value={metricId}
                                placeholder={placeholder || text.fields.metric.placeholder}
                                onChange={val => {
                                    setSubMetricValues([]);
                                    setSubMetricId(null);
                                    setMetricId(val);
                                }}
                                help={showFormHelp ? text.fields.metric.help : ''}
                                data={[...filteredMetrics.map(m => ({ ...m, id: metricSafeId(m), extra: metricExtra(m) }))]}
                            />
                        </div>
                        {hasModule(user, 'integrations') &&
                            !hideSubMetrics &&
                            !selectedMetric?.ratio_Id1 &&
                            selectedMetric?.extra_Fields?.length > 0 && (
                                <div className="second">
                                    <Forms.Select
                                        label={null}
                                        id="select-second"
                                        value={subMetricId}
                                        placeholder={text.fields.field.placeholder}
                                        onChange={val => {
                                            setSubMetricValues([]);
                                            setSubMetricId(val);
                                        }}
                                        data={selectedMetric.extra_Fields}
                                    />
                                </div>
                            )}
                        {hasModule(user, 'integrations') &&
                            ArrayHelpers.existsAndHasLength(subMetrics) &&
                            !hideSubMetrics &&
                            (subMetricId || 0) > 0 && (
                                <div className="third">
                                    <Forms.Select
                                        label={null}
                                        id="select-third"
                                        value={subMetricValues}
                                        allowMultiple
                                        placeholder={text.fields.value.placeholder}
                                        onChange={val => setSubMetricValues(val)}
                                        data={subMetrics}
                                    />
                                </div>
                            )}
                    </div>
                </div>
                {error && <span className="help-block mt0">{error}</span>}
            </div>
            {showAverage && (
                <Forms.Buttons
                    label="How would you like the data to display?"
                    help="You can chose to either display the total for all users of the average per user."
                >
                    <button
                        disabled={averageMetric}
                        className={`btn btn-${!average ? 'primary' : 'default'}`}
                        onClick={() => setAverage(false)}
                    >
                        {text.fields.average.total}
                    </button>
                    <button
                        disabled={averageMetric}
                        className={`btn btn-${average ? 'primary' : 'default'}`}
                        onClick={() => setAverage(true)}
                    >
                        {text.fields.average.average}
                    </button>
                </Forms.Buttons>
            )}
        </div>
    );
};

export default MetricPicker;
